import React from 'react';
import { EyeOff } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Pourquoi un bloc de l'onglet Analytics reste vide.
 *
 * ## ⚠️ Le défaut que ce composant ferme
 *
 * Trois blocs — répartition démographique, répartition géographique, montants dépensés —
 * étaient rendus sous `stats.X && stats.X.length > 0 && (…)`. Ils ne s'affichaient donc
 * **jamais** : `demographicDistribution` et `deliveryByRegion` valent **0 sur 231 lignes**
 * mesurées en base, et `spend` n'existe que pour les publicités politiques. Les trois
 * disparaissaient en silence.
 *
 * Un bloc absent se lit « rien à signaler ». Or il n'y a rien *à cet endroit* parce que Meta ne
 * le publie pas — un « pas mesuré », qui n'est pas la même chose. C'est la distinction que tout
 * le reste du projet tient : `SKIPPED` contre `GREEN`, `mesureIndisponible`, `note_vide`. Elle
 * manquait ici, et la conséquence était qu'on cherchait une panne dans Kokpit.
 *
 * ⚠️ **Le dénominateur fait partie du message.** « 0 » tout court est une panne possible ;
 * « aucune des 88 créas de ce concurrent » est un constat. C'est le serveur qui rédige la
 * phrase (`mesuresReservees` dans `routes/competitors/ciblageDsa.js`) : l'écrire ici en
 * ferait une seconde rédaction, qui divergerait.
 */
const MesureNonPubliee = ({ titre, bloc }) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  if (!bloc) return null;

  return (
    <div style={{
      padding: '20px',
      borderRadius: '12px',
      backgroundColor: '#fafafa',
      border: '1px dashed #d1d5db'
    }}>
      <h3 style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '600',
        marginBottom: '8px',
        color: '#6b7280'
      }}>
        <EyeOff size={16} />
        {titre}
      </h3>

      <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
        {bloc.message}
      </p>

      {/* Le dénominateur, à l'écran et non seulement dans la phrase : c'est lui qui distingue
          « Meta ne le publie pas » d'une collecte qui n'aurait rien ramené. */}
      {bloc.creasTotal > 0 && (
        <p
          className="ksi-survolable"
          onMouseEnter={survoler(
            "Ces mesures existent dans l'API de la bibliothèque publicitaire, mais Meta ne les "
            + "renseigne que pour les publicités à caractère politique ou sociétal. Pour un "
            + "annonceur commercial, elles restent vides — ce n'est pas un défaut de collecte."
          )}
          onMouseLeave={quitter}
          style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px', marginBottom: 0 }}
        >
          {bloc.creasAvecMesure} créa sur {bloc.creasTotal} porte cette mesure
        </p>
      )}

      {bulle}
    </div>
  );
};

export default MesureNonPubliee;
